import React, { useState, useCallback, useEffect, useRef } from 'react';
import { WarehouseMapCanvas } from './WarehouseMapCanvas';
import { MapToolbar, MapSettingsPanel } from './MapToolbar';
import { MapSearchPanel } from './MapSearchPanel';
import { Button } from '../ui/Button';
import type {
  Warehouse,
  WarehouseZone,
  WarehouseRack,
  WarehouseBin,
  WarehouseLayout,
  ViewportState,
  Position,
  MapEditorTool,
  MapEditorConfig,
  ZoneUpdateData,
  RackUpdateData,
} from '../../types/warehouse';
import { DEFAULT_GRID_SIZE, ZONE_COLORS } from '../../types/warehouse';
import {
  calculateAutoLayoutForZones,
  calculateAutoLayoutForRacks,
  getNextAvailableZoneNumber,
  getNextAvailableZoneColor,
  formatZoneCode,
} from '../../utils/warehouseUtils';
import {
  Map,
  Settings,
  Layers,
  Grid3X3,
  ChevronRight,
  Info,
  X,
  Save,
  RotateCcw,
  Wand2,
  Search,
  Undo2,
  Redo2,
  Keyboard,
  Magnet,
  Ruler,
  AlertTriangle,
} from 'lucide-react';

interface WarehouseMapTabProps {
  warehouse: Warehouse;
  zones: WarehouseZone[];
  racks: WarehouseRack[];
  bins: WarehouseBin[];
  layout: WarehouseLayout | null;
  onUpdateLayout?: (viewport: ViewportState) => Promise<void>;
  onUpdateZone?: (id: string, data: ZoneUpdateData) => Promise<WarehouseZone | null>;
  onUpdateRack?: (id: string, data: RackUpdateData) => Promise<WarehouseRack | null>;
  onCreateZone?: (data: {
    warehouse_id: string;
    zone_name?: string;
    color: string;
    x_position: number;
    y_position: number;
    zone_width: number;
    zone_height: number;
  }) => Promise<WarehouseZone | null>;
  onCreateRack?: (data: {
    warehouse_id: string;
    rack_name?: string;
    x_position: number;
    y_position: number;
  }) => Promise<WarehouseRack | null>;
}

const DEFAULT_CONFIG: MapEditorConfig = {
  activeTool: 'select',
  showZoneLabels: true,
  showRackLabels: true,
  showBinCounts: false,
  zoneOpacity: 0.7,
};

interface HistoryEntry {
  type: 'zone-move' | 'zone-resize' | 'rack-move';
  id: string;
  previousData: {
    x_position?: number;
    y_position?: number;
    zone_width?: number;
    zone_height?: number;
  };
  newData: {
    x_position?: number;
    y_position?: number;
    zone_width?: number;
    zone_height?: number;
  };
}

const MAX_HISTORY = 50;
const SCALE_FACTOR = 3;

export const WarehouseMapTab: React.FC<WarehouseMapTabProps> = ({
  warehouse,
  zones,
  racks,
  bins,
  layout,
  onUpdateLayout,
  onUpdateZone,
  onUpdateRack,
  onCreateZone,
  onCreateRack,
}) => {
  const [config, setConfig] = useState<MapEditorConfig>(DEFAULT_CONFIG);
  const [showGrid, setShowGrid] = useState(true);
  const [gridSize, setGridSize] = useState(DEFAULT_GRID_SIZE);
  const [snapToGridEnabled, setSnapToGridEnabled] = useState(true);
  const [showRulers, setShowRulers] = useState(true);
  const [showCollisionWarnings, setShowCollisionWarnings] = useState(true);
  const [selectedZoneId, setSelectedZoneId] = useState<string | null>(null);
  const [selectedRackId, setSelectedRackId] = useState<string | null>(null);
  const [showSettingsPanel, setShowSettingsPanel] = useState(false);
  const [showSearchPanel, setShowSearchPanel] = useState(false);
  const [showKeyboardShortcuts, setShowKeyboardShortcuts] = useState(false);
  const [viewport, setViewport] = useState<ViewportState>(
    layout?.viewport_json || { x: 50, y: 50, scale: 1 }
  );
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [saving, setSaving] = useState(false);
  const [autoLayoutLoading, setAutoLayoutLoading] = useState(false);

  const [history, setHistory] = useState<HistoryEntry[]>([]);
  const [historyIndex, setHistoryIndex] = useState(-1);

  const containerRef = useRef<HTMLDivElement>(null);

  const selectedZone = zones.find((z) => z.id === selectedZoneId);
  const selectedRack = racks.find((r) => r.id === selectedRackId);

  const canUndo = historyIndex >= 0;
  const canRedo = historyIndex < history.length - 1;

  const addToHistory = useCallback((entry: HistoryEntry) => {
    setHistory((prev) => {
      const newHistory = prev.slice(0, historyIndex + 1);
      newHistory.push(entry);
      if (newHistory.length > MAX_HISTORY) {
        newHistory.shift();
      }
      return newHistory;
    });
    setHistoryIndex((prev) => Math.min(prev + 1, MAX_HISTORY - 1));
  }, [historyIndex]);

  const handleUndo = useCallback(async () => {
    if (!canUndo || historyIndex < 0) return;

    const entry = history[historyIndex];
    if (entry.type === 'zone-move' || entry.type === 'zone-resize') {
      if (onUpdateZone) {
        await onUpdateZone(entry.id, entry.previousData);
      }
    } else if (entry.type === 'rack-move') {
      if (onUpdateRack) {
        await onUpdateRack(entry.id, entry.previousData);
      }
    }
    setHistoryIndex((prev) => prev - 1);
  }, [canUndo, history, historyIndex, onUpdateZone, onUpdateRack]);

  const handleRedo = useCallback(async () => {
    if (!canRedo || historyIndex >= history.length - 1) return;

    const nextIndex = historyIndex + 1;
    const entry = history[nextIndex];
    if (entry.type === 'zone-move' || entry.type === 'zone-resize') {
      if (onUpdateZone) {
        await onUpdateZone(entry.id, entry.newData);
      }
    } else if (entry.type === 'rack-move') {
      if (onUpdateRack) {
        await onUpdateRack(entry.id, entry.newData);
      }
    }
    setHistoryIndex(nextIndex);
  }, [canRedo, history, historyIndex, onUpdateZone, onUpdateRack]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) {
        return;
      }

      if ((e.metaKey || e.ctrlKey) && e.key === 'z') {
        e.preventDefault();
        if (e.shiftKey) {
          handleRedo();
        } else {
          handleUndo();
        }
        return;
      }

      if ((e.metaKey || e.ctrlKey) && e.key === 'y') {
        e.preventDefault();
        handleRedo();
        return;
      }

      switch (e.key.toLowerCase()) {
        case 'v':
          setConfig((prev) => ({ ...prev, activeTool: 'select' }));
          break;
        case 'h':
          setConfig((prev) => ({ ...prev, activeTool: 'pan' }));
          break;
        case 'z':
          if (!e.metaKey && !e.ctrlKey) {
            setConfig((prev) => ({ ...prev, activeTool: 'zone' }));
          }
          break;
        case 'r':
          setConfig((prev) => ({ ...prev, activeTool: 'rack' }));
          break;
        case 'g':
          setShowGrid((prev) => !prev);
          break;
        case 's':
          if (!e.metaKey && !e.ctrlKey) {
            setSnapToGridEnabled((prev) => !prev);
          }
          break;
        case 'l':
          setConfig((prev) => ({ ...prev, showZoneLabels: !prev.showZoneLabels }));
          break;
        case 'k':
          setConfig((prev) => ({ ...prev, showRackLabels: !prev.showRackLabels }));
          break;
        case '?':
          setShowKeyboardShortcuts((prev) => !prev);
          break;
        case 'escape':
          setSelectedZoneId(null);
          setSelectedRackId(null);
          setConfig((prev) => ({ ...prev, activeTool: 'select' }));
          break;
        case 'delete':
        case 'backspace':
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleUndo, handleRedo]);

  const handleViewportChange = useCallback((newViewport: ViewportState) => {
    setViewport(newViewport);
    setHasUnsavedChanges(true);
  }, []);

  const handleZoneMove = useCallback(
    async (zoneId: string, position: Position) => {
      if (onUpdateZone) {
        const zone = zones.find((z) => z.id === zoneId);
        if (zone) {
          const previousData = { x_position: zone.x_position, y_position: zone.y_position };
          const newData = { x_position: Math.round(position.x), y_position: Math.round(position.y) };

          await onUpdateZone(zoneId, newData);

          addToHistory({
            type: 'zone-move',
            id: zoneId,
            previousData,
            newData,
          });

          setHasUnsavedChanges(true);
        }
      }
    },
    [onUpdateZone, zones, addToHistory]
  );

  const handleZoneResize = useCallback(
    async (zoneId: string, width: number, height: number, position?: Position) => {
      if (onUpdateZone) {
        const zone = zones.find((z) => z.id === zoneId);
        if (zone) {
          const previousData = {
            x_position: zone.x_position,
            y_position: zone.y_position,
            zone_width: zone.zone_width,
            zone_height: zone.zone_height,
          };
          const newData = {
            x_position: position ? Math.round(position.x) : zone.x_position,
            y_position: position ? Math.round(position.y) : zone.y_position,
            zone_width: Math.round(width),
            zone_height: Math.round(height),
          };

          await onUpdateZone(zoneId, newData);

          addToHistory({
            type: 'zone-resize',
            id: zoneId,
            previousData,
            newData,
          });

          setHasUnsavedChanges(true);
        }
      }
    },
    [onUpdateZone, zones, addToHistory]
  );

  const handleRackMove = useCallback(
    async (rackId: string, position: Position) => {
      if (onUpdateRack) {
        const rack = racks.find((r) => r.id === rackId);
        if (rack) {
          const previousData = { x_position: rack.x_position, y_position: rack.y_position };
          const newData = { x_position: Math.round(position.x), y_position: Math.round(position.y) };

          await onUpdateRack(rackId, newData);

          addToHistory({
            type: 'rack-move',
            id: rackId,
            previousData,
            newData,
          });

          setHasUnsavedChanges(true);
        }
      }
    },
    [onUpdateRack, racks, addToHistory]
  );

  const handleZoneCreate = useCallback(
    async (position: Position) => {
      if (!onCreateZone) return;

      const zoneNumber = getNextAvailableZoneNumber(zones);
      const zoneColor = getNextAvailableZoneColor(zones, ZONE_COLORS);

      await onCreateZone({
        warehouse_id: warehouse.id,
        zone_name: `Zone ${zoneNumber}`,
        color: zoneColor,
        x_position: Math.round(position.x),
        y_position: Math.round(position.y),
        zone_width: 20,
        zone_height: 20,
      });

      setConfig((prev) => ({ ...prev, activeTool: 'select' }));
      setHasUnsavedChanges(true);
    },
    [onCreateZone, warehouse.id, zones]
  );

  const handleRackCreate = useCallback(
    async (position: Position) => {
      if (!onCreateRack) return;

      await onCreateRack({
        warehouse_id: warehouse.id,
        x_position: Math.round(position.x),
        y_position: Math.round(position.y),
      });

      setConfig((prev) => ({ ...prev, activeTool: 'select' }));
      setHasUnsavedChanges(true);
    },
    [onCreateRack, warehouse.id]
  );

  const handleSaveLayout = useCallback(async () => {
    if (!onUpdateLayout) return;

    setSaving(true);
    try {
      await onUpdateLayout(viewport);
      setHasUnsavedChanges(false);
    } catch (error) {
      console.error('Failed to save layout:', error);
    } finally {
      setSaving(false);
    }
  }, [onUpdateLayout, viewport]);

  const handleFitToContent = useCallback(() => {
    if (zones.length === 0 && racks.length === 0) {
      setViewport({ x: 50, y: 50, scale: 1 });
      return;
    }

    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;

    for (const zone of zones) {
      minX = Math.min(minX, zone.x_position);
      minY = Math.min(minY, zone.y_position);
      maxX = Math.max(maxX, zone.x_position + zone.zone_width);
      maxY = Math.max(maxY, zone.y_position + zone.zone_height);
    }

    for (const rack of racks) {
      minX = Math.min(minX, rack.x_position);
      minY = Math.min(minY, rack.y_position);
      maxX = Math.max(maxX, rack.x_position + rack.rack_length);
      maxY = Math.max(maxY, rack.y_position + rack.rack_depth);
    }

    if (minX === Infinity) {
      setViewport({ x: 50, y: 50, scale: 1 });
      return;
    }

    const container = containerRef.current;
    const canvasWidth = container?.clientWidth || 800;
    const canvasHeight = container?.clientHeight || 500;

    const padding = 40;
    const rulerOffset = showRulers ? 24 : 0;
    const availableWidth = canvasWidth - padding * 2 - rulerOffset;
    const availableHeight = canvasHeight - padding * 2 - rulerOffset;

    const contentWidth = (maxX - minX) * SCALE_FACTOR;
    const contentHeight = (maxY - minY) * SCALE_FACTOR;

    const scaleX = availableWidth / contentWidth;
    const scaleY = availableHeight / contentHeight;
    const newScale = Math.max(0.1, Math.min(3, Math.min(scaleX, scaleY) * 0.9));

    const contentCenterX = ((minX + maxX) / 2) * SCALE_FACTOR;
    const contentCenterY = ((minY + maxY) / 2) * SCALE_FACTOR;

    const newX = (canvasWidth - rulerOffset) / 2 - contentCenterX * newScale;
    const newY = (canvasHeight - rulerOffset) / 2 - contentCenterY * newScale;

    setViewport({ x: newX, y: newY, scale: newScale });
    setHasUnsavedChanges(true);
  }, [zones, racks, showRulers]);

  const handleResetView = useCallback(() => {
    setViewport({ x: 50, y: 50, scale: 1 });
    setHasUnsavedChanges(true);
  }, []);

  const handleAutoLayout = useCallback(async () => {
    if (!onUpdateZone || !onUpdateRack) return;

    setAutoLayoutLoading(true);

    try {
      const layoutConfig = {
        warehouseLength: warehouse.length,
        warehouseWidth: warehouse.width,
        padding: 5,
        zoneSpacing: 3,
        rackSpacing: 2,
        aisleWidth: 4,
      };

      if (zones.length > 0) {
        const zoneLayouts = calculateAutoLayoutForZones(zones, layoutConfig);

        for (const zoneLayout of zoneLayouts) {
          await onUpdateZone(zoneLayout.zoneId, {
            x_position: zoneLayout.x,
            y_position: zoneLayout.y,
            zone_width: zoneLayout.width,
            zone_height: zoneLayout.height,
          });
        }
      }

      if (racks.length > 0) {
        const updatedZones = zones.map((zone) => {
          const layoutResult = calculateAutoLayoutForZones(zones, layoutConfig).find(
            (l) => l.zoneId === zone.id
          );
          if (layoutResult) {
            return {
              ...zone,
              x_position: layoutResult.x,
              y_position: layoutResult.y,
              zone_width: layoutResult.width,
              zone_height: layoutResult.height,
            };
          }
          return zone;
        });

        const rackLayouts = calculateAutoLayoutForRacks(racks, updatedZones, layoutConfig);

        for (const rackLayout of rackLayouts) {
          await onUpdateRack(rackLayout.rackId, {
            x_position: rackLayout.x,
            y_position: rackLayout.y,
            rotation: rackLayout.rotation,
          });
        }
      }

      setHasUnsavedChanges(true);

      setTimeout(() => {
        handleFitToContent();
      }, 100);
    } catch (error) {
      console.error('Auto layout failed:', error);
    } finally {
      setAutoLayoutLoading(false);
    }
  }, [warehouse, zones, racks, onUpdateZone, onUpdateRack, handleFitToContent]);

  const handleCenterOnZone = useCallback((zone: WarehouseZone) => {
    const container = containerRef.current;
    const canvasWidth = container?.clientWidth || 800;
    const canvasHeight = container?.clientHeight || 500;
    const rulerOffset = showRulers ? 24 : 0;

    const zoneX = zone.x_position * SCALE_FACTOR;
    const zoneY = zone.y_position * SCALE_FACTOR;
    const zoneWidth = zone.zone_width * SCALE_FACTOR;
    const zoneHeight = zone.zone_height * SCALE_FACTOR;

    const centerX = zoneX + zoneWidth / 2;
    const centerY = zoneY + zoneHeight / 2;

    setViewport((prev) => ({
      x: (canvasWidth - rulerOffset) / 2 - centerX * prev.scale,
      y: (canvasHeight - rulerOffset) / 2 - centerY * prev.scale,
      scale: prev.scale,
    }));
  }, [showRulers]);

  const handleCenterOnRack = useCallback((rack: WarehouseRack) => {
    const container = containerRef.current;
    const canvasWidth = container?.clientWidth || 800;
    const canvasHeight = container?.clientHeight || 500;
    const rulerOffset = showRulers ? 24 : 0;

    const rackX = rack.x_position * SCALE_FACTOR;
    const rackY = rack.y_position * SCALE_FACTOR;
    const rackWidth = rack.rack_length * SCALE_FACTOR;
    const rackHeight = rack.rack_depth * SCALE_FACTOR;

    const centerX = rackX + rackWidth / 2;
    const centerY = rackY + rackHeight / 2;

    setViewport((prev) => ({
      x: (canvasWidth - rulerOffset) / 2 - centerX * prev.scale,
      y: (canvasHeight - rulerOffset) / 2 - centerY * prev.scale,
      scale: prev.scale,
    }));
  }, [showRulers]);

  if (zones.length === 0 && racks.length === 0) {
    return (
      <div className="text-center py-16">
        <div className="w-20 h-20 bg-blue-100 dark:bg-blue-900/30 rounded-2xl flex items-center justify-center mx-auto mb-6">
          <Map className="h-10 w-10 text-blue-600 dark:text-blue-400" />
        </div>
        <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2">
          Visual Map Ready
        </h3>
        <p className="text-gray-600 dark:text-gray-400 mb-6 max-w-md mx-auto">
          Create zones and racks first to start placing them on the visual map.
          The map will show your warehouse layout once you have items to place.
        </p>
        <div className="flex items-center justify-center gap-4">
          <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
            <Layers className="h-4 w-4" />
            <span>{zones.length} zones</span>
          </div>
          <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
            <Grid3X3 className="h-4 w-4" />
            <span>{racks.length} racks</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            Visual Map Editor
          </h3>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Drag zones and racks to arrange your warehouse layout
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {hasUnsavedChanges && (
            <span className="text-sm text-amber-600 dark:text-amber-400 flex items-center gap-1">
              <span className="w-2 h-2 bg-amber-500 rounded-full animate-pulse" />
              Unsaved changes
            </span>
          )}

          <div className="flex items-center gap-1 border-r border-gray-200 dark:border-gray-700 pr-2 mr-1">
            <button
              onClick={handleUndo}
              disabled={!canUndo}
              className={`p-2 rounded-lg transition-colors ${
                canUndo
                  ? 'hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-400'
                  : 'text-gray-300 dark:text-gray-600 cursor-not-allowed'
              }`}
              title="Undo (Ctrl+Z)"
            >
              <Undo2 className="h-4 w-4" />
            </button>
            <button
              onClick={handleRedo}
              disabled={!canRedo}
              className={`p-2 rounded-lg transition-colors ${
                canRedo
                  ? 'hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-400'
                  : 'text-gray-300 dark:text-gray-600 cursor-not-allowed'
              }`}
              title="Redo (Ctrl+Shift+Z)"
            >
              <Redo2 className="h-4 w-4" />
            </button>
          </div>

          <Button
            variant="secondary"
            size="sm"
            onClick={() => setShowSearchPanel(!showSearchPanel)}
          >
            <Search className="h-4 w-4 mr-2" />
            Search
          </Button>
          <Button
            variant="secondary"
            size="sm"
            onClick={handleAutoLayout}
            loading={autoLayoutLoading}
            title="Automatically arrange zones and racks"
          >
            <Wand2 className="h-4 w-4 mr-2" />
            Auto Layout
          </Button>
          <Button
            variant="secondary"
            size="sm"
            onClick={handleFitToContent}
            title="Fit all content in view"
          >
            <RotateCcw className="h-4 w-4 mr-2" />
            Fit View
          </Button>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => setShowSettingsPanel(!showSettingsPanel)}
          >
            <Settings className="h-4 w-4 mr-2" />
            Settings
          </Button>
          {onUpdateLayout && (
            <Button
              variant="primary"
              size="sm"
              onClick={handleSaveLayout}
              loading={saving}
              disabled={!hasUnsavedChanges}
            >
              <Save className="h-4 w-4 mr-2" />
              Save Layout
            </Button>
          )}
        </div>
      </div>

      <div className="flex gap-4">
        <MapToolbar
          activeTool={config.activeTool}
          showGrid={showGrid}
          showZoneLabels={config.showZoneLabels}
          showRackLabels={config.showRackLabels}
          gridSize={gridSize}
          zoneOpacity={config.zoneOpacity}
          snapToGrid={snapToGridEnabled}
          showRulers={showRulers}
          showCollisionWarnings={showCollisionWarnings}
          canCreateZone={!!onCreateZone}
          canCreateRack={!!onCreateRack}
          onToolChange={(tool) => setConfig((prev) => ({ ...prev, activeTool: tool }))}
          onShowGridChange={setShowGrid}
          onShowZoneLabelsChange={(show) =>
            setConfig((prev) => ({ ...prev, showZoneLabels: show }))
          }
          onShowRackLabelsChange={(show) =>
            setConfig((prev) => ({ ...prev, showRackLabels: show }))
          }
          onGridSizeChange={setGridSize}
          onZoneOpacityChange={(opacity) =>
            setConfig((prev) => ({ ...prev, zoneOpacity: opacity }))
          }
          onSnapToGridChange={setSnapToGridEnabled}
          onShowRulersChange={setShowRulers}
          onShowCollisionWarningsChange={setShowCollisionWarnings}
          onFitToContent={handleFitToContent}
          onResetView={handleResetView}
          onShowKeyboardShortcuts={() => setShowKeyboardShortcuts(true)}
        />

        <div className="flex-1 relative" ref={containerRef}>
          <div className="h-[500px] rounded-xl overflow-hidden border border-gray-200 dark:border-gray-700">
            <WarehouseMapCanvas
              warehouse={warehouse}
              zones={zones}
              racks={racks}
              bins={bins}
              selectedZoneId={selectedZoneId}
              selectedRackId={selectedRackId}
              activeTool={config.activeTool}
              showGrid={showGrid}
              gridSize={gridSize}
              showZoneLabels={config.showZoneLabels}
              showRackLabels={config.showRackLabels}
              showBinCounts={config.showBinCounts}
              showRulers={showRulers}
              zoneOpacity={config.zoneOpacity}
              snapToGridEnabled={snapToGridEnabled}
              showCollisionWarnings={showCollisionWarnings}
              viewport={viewport}
              onViewportChange={handleViewportChange}
              onZoneSelect={setSelectedZoneId}
              onRackSelect={setSelectedRackId}
              onZoneMove={handleZoneMove}
              onZoneResize={handleZoneResize}
              onRackMove={handleRackMove}
              onZoneCreate={onCreateZone ? handleZoneCreate : undefined}
              onRackCreate={onCreateRack ? handleRackCreate : undefined}
            />
          </div>

          {showSettingsPanel && (
            <div className="absolute top-4 right-4 z-10">
              <div className="relative">
                <button
                  onClick={() => setShowSettingsPanel(false)}
                  className="absolute -top-2 -right-2 w-6 h-6 bg-gray-200 dark:bg-gray-700 rounded-full flex items-center justify-center text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 z-10"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
                <MapSettingsPanel
                  showGrid={showGrid}
                  gridSize={gridSize}
                  zoneOpacity={config.zoneOpacity}
                  snapToGrid={snapToGridEnabled}
                  showRulers={showRulers}
                  showCollisionWarnings={showCollisionWarnings}
                  onShowGridChange={setShowGrid}
                  onGridSizeChange={setGridSize}
                  onZoneOpacityChange={(opacity) =>
                    setConfig((prev) => ({ ...prev, zoneOpacity: opacity }))
                  }
                  onSnapToGridChange={setSnapToGridEnabled}
                  onShowRulersChange={setShowRulers}
                  onShowCollisionWarningsChange={setShowCollisionWarnings}
                />
              </div>
            </div>
          )}

          {showSearchPanel && (
            <div className="absolute top-4 left-4 z-10">
              <MapSearchPanel
                zones={zones}
                racks={racks}
                bins={bins}
                onSelectZone={(id) => {
                  setSelectedZoneId(id);
                  setSelectedRackId(null);
                }}
                onSelectRack={(id) => {
                  setSelectedRackId(id);
                  setSelectedZoneId(null);
                }}
                onCenterOnZone={handleCenterOnZone}
                onCenterOnRack={handleCenterOnRack}
                onClose={() => setShowSearchPanel(false)}
              />
            </div>
          )}
        </div>

        <div className="w-64 space-y-4">
          <SelectionPanel
            selectedZone={selectedZone}
            selectedRack={selectedRack}
            zones={zones}
            bins={bins}
            onClearSelection={() => {
              setSelectedZoneId(null);
              setSelectedRackId(null);
            }}
          />

          <div className="bg-gray-50 dark:bg-gray-800/50 rounded-xl p-4">
            <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-3 flex items-center gap-2">
              <Info className="h-4 w-4 text-blue-500" />
              Tips
            </h4>
            <ul className="text-xs text-gray-600 dark:text-gray-400 space-y-2">
              <li className="flex items-start gap-2">
                <ChevronRight className="h-3 w-3 mt-0.5 text-gray-400" />
                <span>Click to select zones or racks</span>
              </li>
              <li className="flex items-start gap-2">
                <ChevronRight className="h-3 w-3 mt-0.5 text-gray-400" />
                <span>Drag selected items to move them</span>
              </li>
              <li className="flex items-start gap-2">
                <ChevronRight className="h-3 w-3 mt-0.5 text-gray-400" />
                <span>Drag corner handles to resize zones</span>
              </li>
              <li className="flex items-start gap-2">
                <ChevronRight className="h-3 w-3 mt-0.5 text-gray-400" />
                <span>Scroll to zoom in/out</span>
              </li>
              <li className="flex items-start gap-2">
                <ChevronRight className="h-3 w-3 mt-0.5 text-gray-400" />
                <span>Hold Shift + drag to pan</span>
              </li>
              <li className="flex items-start gap-2">
                <ChevronRight className="h-3 w-3 mt-0.5 text-gray-400" />
                <span>Press ? for keyboard shortcuts</span>
              </li>
            </ul>
          </div>

          <div className="bg-gray-50 dark:bg-gray-800/50 rounded-xl p-4">
            <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-3 flex items-center gap-2">
              <Keyboard className="h-4 w-4 text-blue-500" />
              Quick Keys
            </h4>
            <div className="grid grid-cols-2 gap-2 text-xs">
              <div className="flex items-center gap-2">
                <kbd className="px-1.5 py-0.5 bg-gray-200 dark:bg-gray-700 rounded text-gray-700 dark:text-gray-300 font-mono">V</kbd>
                <span className="text-gray-600 dark:text-gray-400">Select</span>
              </div>
              <div className="flex items-center gap-2">
                <kbd className="px-1.5 py-0.5 bg-gray-200 dark:bg-gray-700 rounded text-gray-700 dark:text-gray-300 font-mono">H</kbd>
                <span className="text-gray-600 dark:text-gray-400">Pan</span>
              </div>
              <div className="flex items-center gap-2">
                <kbd className="px-1.5 py-0.5 bg-gray-200 dark:bg-gray-700 rounded text-gray-700 dark:text-gray-300 font-mono">Z</kbd>
                <span className="text-gray-600 dark:text-gray-400">Zone</span>
              </div>
              <div className="flex items-center gap-2">
                <kbd className="px-1.5 py-0.5 bg-gray-200 dark:bg-gray-700 rounded text-gray-700 dark:text-gray-300 font-mono">R</kbd>
                <span className="text-gray-600 dark:text-gray-400">Rack</span>
              </div>
              <div className="flex items-center gap-2">
                <kbd className="px-1.5 py-0.5 bg-gray-200 dark:bg-gray-700 rounded text-gray-700 dark:text-gray-300 font-mono">G</kbd>
                <span className="text-gray-600 dark:text-gray-400">Grid</span>
              </div>
              <div className="flex items-center gap-2">
                <kbd className="px-1.5 py-0.5 bg-gray-200 dark:bg-gray-700 rounded text-gray-700 dark:text-gray-300 font-mono">S</kbd>
                <span className="text-gray-600 dark:text-gray-400">Snap</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {showKeyboardShortcuts && (
        <KeyboardShortcutsModal onClose={() => setShowKeyboardShortcuts(false)} />
      )}
    </div>
  );
};

interface SelectionPanelProps {
  selectedZone: WarehouseZone | undefined;
  selectedRack: WarehouseRack | undefined;
  zones: WarehouseZone[];
  bins: WarehouseBin[];
  onClearSelection: () => void;
}

const SelectionPanel: React.FC<SelectionPanelProps> = ({
  selectedZone,
  selectedRack,
  zones,
  bins,
  onClearSelection,
}) => {
  if (!selectedZone && !selectedRack) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
        <div className="text-center text-gray-500 dark:text-gray-400 py-4">
          <MousePointerIcon className="h-8 w-8 mx-auto mb-2 opacity-50" />
          <p className="text-sm">Select a zone or rack to view details</p>
        </div>
      </div>
    );
  }

  if (selectedZone) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
        <div
          className="px-4 py-3 border-b border-gray-200 dark:border-gray-700"
          style={{ backgroundColor: `${selectedZone.color}20` }}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span
                className="w-4 h-4 rounded"
                style={{ backgroundColor: selectedZone.color }}
              />
              <span className="font-semibold text-gray-900 dark:text-gray-100">
                Zone {selectedZone.zone_code}
              </span>
            </div>
            <button
              onClick={onClearSelection}
              className="p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-700"
            >
              <X className="h-4 w-4 text-gray-500" />
            </button>
          </div>
          {selectedZone.zone_name && (
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              {selectedZone.zone_name}
            </p>
          )}
        </div>
        <div className="p-4 space-y-3 text-sm">
          <div className="flex justify-between">
            <span className="text-gray-500 dark:text-gray-400">Position</span>
            <span className="text-gray-900 dark:text-gray-100">
              ({selectedZone.x_position}, {selectedZone.y_position})
            </span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-500 dark:text-gray-400">Size</span>
            <span className="text-gray-900 dark:text-gray-100">
              {selectedZone.zone_width} x {selectedZone.zone_height}
            </span>
          </div>
        </div>
      </div>
    );
  }

  if (selectedRack) {
    const zone = zones.find((z) => z.id === selectedRack.zone_id);
    const rackBins = bins.filter((b) => b.rack_id === selectedRack.id);

    return (
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700 bg-emerald-50 dark:bg-emerald-900/20">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Grid3X3 className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
              <span className="font-semibold text-gray-900 dark:text-gray-100">
                {selectedRack.rack_code}
              </span>
            </div>
            <button
              onClick={onClearSelection}
              className="p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-700"
            >
              <X className="h-4 w-4 text-gray-500" />
            </button>
          </div>
          {selectedRack.rack_name && (
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              {selectedRack.rack_name}
            </p>
          )}
        </div>
        <div className="p-4 space-y-3 text-sm">
          <div className="flex justify-between">
            <span className="text-gray-500 dark:text-gray-400">Zone</span>
            <span className="text-gray-900 dark:text-gray-100">
              {zone ? (
                <span className="flex items-center gap-1">
                  <span
                    className="w-2.5 h-2.5 rounded-full"
                    style={{ backgroundColor: zone.color }}
                  />
                  {zone.zone_code}
                </span>
              ) : (
                <span className="text-gray-400 italic">Unassigned</span>
              )}
            </span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-500 dark:text-gray-400">Position</span>
            <span className="text-gray-900 dark:text-gray-100">
              ({selectedRack.x_position}, {selectedRack.y_position})
            </span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-500 dark:text-gray-400">Configuration</span>
            <span className="text-gray-900 dark:text-gray-100">
              {selectedRack.levels}L x {selectedRack.bays}B
            </span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-500 dark:text-gray-400">Bins</span>
            <span className="text-gray-900 dark:text-gray-100">
              {rackBins.length} / {selectedRack.levels * selectedRack.bays}
            </span>
          </div>
        </div>
      </div>
    );
  }

  return null;
};

const MousePointerIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={1.5}
      d="M3 3l7.07 16.97 2.51-7.39 7.39-2.51L3 3z"
    />
  </svg>
);

interface KeyboardShortcutsModalProps {
  onClose: () => void;
}

const KeyboardShortcutsModal: React.FC<KeyboardShortcutsModalProps> = ({ onClose }) => {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl max-w-md w-full overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
            <Keyboard className="h-5 w-5 text-blue-500" />
            Keyboard Shortcuts
          </h3>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="p-6 space-y-6 max-h-[60vh] overflow-y-auto">
          <div>
            <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-3">Tools</h4>
            <div className="space-y-2">
              <ShortcutRow keys={['V']} description="Select tool" />
              <ShortcutRow keys={['H']} description="Pan tool" />
              <ShortcutRow keys={['Z']} description="Zone tool" />
              <ShortcutRow keys={['R']} description="Rack tool" />
            </div>
          </div>
          <div>
            <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-3">View</h4>
            <div className="space-y-2">
              <ShortcutRow keys={['G']} description="Toggle grid" />
              <ShortcutRow keys={['S']} description="Toggle snap to grid" />
              <ShortcutRow keys={['L']} description="Toggle zone labels" />
              <ShortcutRow keys={['K']} description="Toggle rack labels" />
            </div>
          </div>
          <div>
            <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-3">Actions</h4>
            <div className="space-y-2">
              <ShortcutRow keys={['Ctrl', 'Z']} description="Undo" />
              <ShortcutRow keys={['Ctrl', 'Shift', 'Z']} description="Redo" />
              <ShortcutRow keys={['Esc']} description="Deselect / Cancel" />
            </div>
          </div>
          <div>
            <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-3">Navigation</h4>
            <div className="space-y-2">
              <ShortcutRow keys={['Scroll']} description="Zoom in/out" />
              <ShortcutRow keys={['Shift', 'Drag']} description="Pan view" />
              <ShortcutRow keys={['?']} description="Show shortcuts" />
            </div>
          </div>
        </div>
        <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
          <Button variant="secondary" className="w-full" onClick={onClose}>
            Close
          </Button>
        </div>
      </div>
    </div>
  );
};

interface ShortcutRowProps {
  keys: string[];
  description: string;
}

const ShortcutRow: React.FC<ShortcutRowProps> = ({ keys, description }) => (
  <div className="flex items-center justify-between">
    <div className="flex items-center gap-1">
      {keys.map((key, i) => (
        <React.Fragment key={key}>
          {i > 0 && <span className="text-gray-400 text-xs">+</span>}
          <kbd className="px-2 py-1 bg-gray-100 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded text-xs font-mono text-gray-700 dark:text-gray-300">
            {key}
          </kbd>
        </React.Fragment>
      ))}
    </div>
    <span className="text-sm text-gray-600 dark:text-gray-400">{description}</span>
  </div>
);
